import { useEffect, useRef } from 'react';

import RoisBackgroundLocation from '../plugins/roisBackgroundLocation';
import { isNativeApp } from '../utils/positionSource';

/**
 * Pide el permiso de notificaciones al entrar en la app, no en la carretera.
 *
 * ─── El agujero que tapa ───────────────────────────────────────────────────
 *
 * El único sitio que pedía `POST_NOTIFICATIONS` era `start()`, o sea **al
 * iniciar el primer tramo**. En una tablet recién preparada eso significa que
 * el diálogo llega en el peor momento posible: el operario ya está en el coche,
 * saliendo, con guantes y con la pantalla al sol.
 *
 * Y llegaba desacoplado de lo demás, lo que despista de verdad: el diálogo de
 * UBICACIÓN sí aparece antes, pero no lo saca este plugin — lo saca el WebView
 * la primera vez que una pantalla llama a `navigator.geolocation` (autolocalizar
 * el PK, el modal de sentido). Ese camino pide ubicación y NADA más. Medido con
 * `adb` en una Tab Active5 el 18-ago-2026: cámara a las 11:15, ubicación a las
 * 11:25, y el de notificaciones sin aparecer jamás — acabó concedido a mano
 * desde Ajustes a las 11:27.
 *
 * ─── Por qué al entrar en el módulo y no al arrancar la app ────────────────
 *
 * Porque Android manda pedir los permisos EN CONTEXTO: «ask for a permission in
 * context, when the user starts to interact with the feature that requires it»,
 * y para las notificaciones insiste en dejar que el usuario conozca antes la
 * app. Un diálogo suelto nada más loguearse se deniega más, y la negativa es
 * PARA SIEMPRE porque Android no lo vuelve a enseñar.
 *
 * El punto de montaje es `SurveillanceRouteLayout`, que envuelve todo el módulo
 * de recorridos: mismo contexto que `start()` pero varios minutos antes — al
 * abrir el listado, con el operario todavía en la oficina.
 *
 * ─── Lo que NO hace, a propósito ───────────────────────────────────────────
 *
 * **No insiste.** Solo pregunta si el estado es `prompt`, o sea si no se ha
 * preguntado nunca. Un `denied` se respeta: Android no volvería a enseñar el
 * diálogo de todas formas, así que reintentar solo serviría para gastar una
 * llamada en cada arranque.
 *
 * **No bloquea nada.** Si falla, se traga el error. La notificación es
 * transparencia para el operario y un atajo para volver a la app; el foreground
 * service corre igual sin ella (verificado: `isForeground=true types=00000008`
 * con el permiso denegado), así que ningún fallo aquí puede impedir que se
 * capture un recorrido.
 *
 * En web es un no-op absoluto: no hay plugin al que preguntar.
 */
export function useNotificationsPermissionPrompt(enabled: boolean): void {
  // Una vez por vida del componente. El overlay está montado en la cabecera y
  // se re-renderiza constantemente (cronómetro); sin esto pediría en bucle.
  const alreadyAsked = useRef(false);

  useEffect(() => {
    if (!enabled || !isNativeApp() || alreadyAsked.current) return;
    alreadyAsked.current = true;

    void (async () => {
      try {
        const status = await RoisBackgroundLocation.checkPermissions();

        // `prompt` es la única señal fiable de "no se ha preguntado nunca":
        // en Ajustes, Android pinta igual eso que un rechazo deliberado.
        const neverAsked =
          status.notifications === 'prompt' ||
          status.notifications === 'prompt-with-rationale';
        if (!neverAsked) return;

        // Solo el alias de notificaciones: la ubicación tiene su momento.
        await RoisBackgroundLocation.requestPermissions({
          permissions: ['notifications'],
        });
      } catch {
        // Accesorio por definición — ver la cabecera.
      }
    })();
  }, [enabled]);
}
